import type React from 'react'
import { cn } from '@meno/ui/lib/utils'

// The section primitives now live in @meno/ui so they are not Screen-Details
// -only — "how a section looks" is an app-wide answer, and keeping the
// definition inside one page's folder is what let the rest of the app drift.
// Re-exported here so every existing importer keeps working unchanged.
import {
  SectionSurface, SectionLabel,
} from '@meno/ui/components/shared/SectionSurface'

export {
  SECTION_SURFACE, SectionSurface, SectionLabel, SECTION_STACK,
} from '@meno/ui/components/shared/SectionSurface'

// Card surface for a single settings group. With `description` + `icon` it
// renders the playlist-style "tile + paragraph" header used by the Ping /
// Wifi-notification cards; with just `title` it renders the plain heading
// used by Orientation / Scaling / Audio.
export function SettingCard({
  title, description, icon: Icon, children,
}: {
  title?:       string
  description?: string
  icon?:        React.ComponentType<{ className?: string }>
  children:     React.ReactNode
}) {
  // Title renders as the same uppercase eyebrow ABOVE the card that the device
  // info, Management and Properties panels use — not a text-base heading inside
  // it. Settings was the only surface in Screen Details doing the latter, so
  // moving between tabs changed what a "section" looked like: eyebrow-above-card
  // in three places, heading-inside-card here, and one bare <h3> in a fourth.
  // One treatment now, and it is the one the rest of the page already reads as
  // a section boundary.
  return (
    <section>
      {title && <SectionLabel>{title}</SectionLabel>}
      <SectionSurface className="p-5 space-y-4">
      {description && (
        <div className="flex items-start gap-4">
          {Icon && (
            <div className="w-14 h-14 rounded-xl bg-muted/40 border border-border flex items-center justify-center shrink-0">
              <Icon className="w-6 h-6 text-muted-foreground" />
            </div>
          )}
          <p className="text-sm text-muted-foreground leading-relaxed pt-1">{description}</p>
        </div>
      )}
      {children}
      </SectionSurface>
    </section>
  )
}

// Toggle-style option button. Active = inverted (foreground bg on background
// text); inactive = outlined with muted text. Used in every Settings card.
export function SettingOption({
  active, onClick, icon: Icon, iconStyle, label,
}: {
  active:    boolean
  onClick:   () => void
  icon?:     React.ComponentType<{ className?: string; style?: React.CSSProperties }>
  /** Optional inline style applied to the icon — used by the rotation
   *  picker to render the icon at the chosen angle so the option
   *  visually hints at the rotation it'll apply. */
  iconStyle?: React.CSSProperties
  label:     string
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`h-12 rounded-xl border text-sm font-medium transition-colors flex items-center justify-center gap-2 px-3 ${
        active
          ? 'bg-foreground text-background border-foreground'
          : 'border-border bg-transparent text-muted-foreground hover:text-foreground hover:bg-muted/40'
      }`}
    >
      {Icon && <Icon className="w-4 h-4" style={iconStyle} />}
      <span className="truncate">{label}</span>
    </button>
  )
}
